// KitDetalle (tienda v5) — port de maquetas/tienda-v5-kit-detalle.html, dinámico por :codigo.
// Códigos válidos: solo, familiar, hogar-plus, duo, confort, exclusive. Inválido → 404 amigable.
// Shared: TopNav/Footer/BottomNav/MiniBar/Breadcrumb/PrecioBox/PagaCon/FaqMini/CrossLinkBanner.
// Page-specific bajo .tv5-page-kit-detalle. Datos desde src/data/kits.js (DRY con Kits.jsx).
// Carrusel: 4:3/max-300px en mobile (fix Fase 1) → precio+CTA above-the-fold. Swipe + dots activos.
//
// Nota: se portaron los elementos de la lista de Nico (carrusel, tags, PrecioBox, CTA, "Ver los N",
// PagaCon, "3 meses", FaqMini, CrossLink). Testimoniales y "comparación con súper" del prototipo
// no estaban en la lista → no se incluyeron (se pueden sumar si Nico los quiere).
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TopNav, Footer, BottomNav, MiniBar, Breadcrumb, PrecioBox, PagaCon, FaqMini, CrossLinkBanner, ScrollHintPill } from '../../components/tienda-v5';
import { TV5_ROUTES } from '../../components/tienda-v5/routes';
import { getKit, ahorroPct, precioBonif } from '../../data/kits';
import { fetchKitsConProductos, normalizarCodigoKit } from '../../lib/tiendaApi';
import { kitViewModel, limpiarPresentacion } from '../../lib/kitsView';

const fmt = (n) => '$' + Number(n).toLocaleString('es-UY');

// TEMP · instrumentación diagnóstico bug P0 "Suscribirme rebota a /catalogo" (prefijo [SUSC]). QUITAR luego.
const dbgSusc = (...a) => { try { console.log('%c[SUSC:kit-detalle]', 'color:#00A8B5;font-weight:700', ...a); } catch { /* noop */ } };

// Foto de producto con fallback a emoji: si no hay URL o la imagen falla al cargar, muestra el emoji.
function FotoKit({ src, emoji, alt, className }) {
  const [err, setErr] = useState(false);
  if (err || !src) return <span aria-hidden="true">{emoji}</span>;
  return <img src={src} alt={alt || ''} className={className} loading="lazy" decoding="async" onError={() => setErr(true)} />;
}

// Ítems de display del kit. Si tenemos los productos REALES de la DB (foto + SKU), los usamos
// (informativo: el kit se compra entero por el CTA, NO ítem por ítem). Si no, fallback al set
// emoji+texto de src/data/kits.js (y se loguea a consola para poder detectar el desajuste).
function buildItems(kit, dbKit) {
  if (dbKit && Array.isArray(dbKit.productos) && dbKit.productos.length) {
    return dbKit.productos.map((p) => ({
      real: true,
      sku: p.sku || null,
      cant: `${Math.round(Number(p.cantidad) || 1)}×`,
      nombre: p.nombre,
      det: limpiarPresentacion(p.presentacion) || p.marca || '',
      imagen: p.imagen_url || null,
      emoji: '🧴',
    }));
  }
  return kit.productos.map((p) => ({
    real: false, sku: null, cant: p.cant, nombre: p.nombre, det: p.det, imagen: null, emoji: p.emoji,
  }));
}

const FAQ_KIT = [
  { q: '¿Puedo cambiar productos del kit?', a: 'Sí, cambios menores dentro del kit (ej. suavizante rosa en lugar de blanco). Marcas específicas dependen del stock. Si algo no te gusta, lo cambiamos por otro equivalente sin cargo.' },
  { q: '¿Cuándo llega el primero?', a: 'Te asignamos al próximo día de ruta de tu zona. Entre 3 y 10 días desde que confirmás. Te avisamos por WhatsApp el día anterior con la franja horaria de 4 hs.' },
  { q: '¿Y si no me alcanza para todo el mes?', a: <>Sumás lo que te falte desde <Link to={TV5_ROUTES.tienda}>La Tienda</Link>. Envío gratis con tu próxima entrega. Si es urgente, te lo mandamos por express (con costo).</> },
];

// Testimoniales genéricos (mismos para todos los kits por ahora — Nico edita el copy después).
const TESTIS = [
  { txt: 'Me ahorra ir al súper cada semana. El primer mes ya no salí más de casa por productos de limpieza.', autor: 'María F.', barrio: 'Cordón' },
  { txt: 'Los productos rinden. Con el Kit Familiar nos duran todo el mes tranquilos.', autor: 'Rodrigo B.', barrio: 'Pocitos' },
  { txt: 'Súper cómodo. Llegó puntual el día que dijeron y era exactamente lo que necesitaba.', autor: 'Ana T.', barrio: 'Punta Carretas' },
];

// Portada del carrusel. Prioridad: (1) foto real del kit; (2) mosaico con las fotos de los
// productos que lo componen; (3) emoji 📦 como último recurso. Antes mostraba SIEMPRE el emoji,
// incluso en los kits que sí tenían foto cargada en la DB.
function PortadaKit({ imagen, nombre, items }) {
  const [err, setErr] = useState(false);
  if (imagen && !err) {
    return <img src={imagen} alt={nombre} className="kit-carousel-slide-img" loading="eager" decoding="async" onError={() => setErr(true)} />;
  }
  const fotos = items.filter((p) => p.imagen).slice(0, 4);
  if (fotos.length >= 3) {
    return (
      <div className="kit-portada-mosaico" aria-label={nombre}>
        {fotos.map((p, i) => (
          <div key={i} className="kit-portada-celda">
            <img src={p.imagen} alt="" loading="lazy" decoding="async" />
          </div>
        ))}
      </div>
    );
  }
  return <span aria-hidden="true">📦</span>;
}

function Carrusel({ kit, items, pct }) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const slides = [{ portada: true }, ...items];
  const total = slides.length;
  // ¿La portada va a mostrar foto (propia o mosaico) en vez del emoji?
  const portadaConFoto = !!kit.imagen || items.filter((p) => p.imagen).length >= 3;

  function onScroll() {
    const el = ref.current;
    if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  }
  function goTo(i) { setIdx(i); const el = ref.current; if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' }); }
  function mover(dir) { const el = ref.current; if (!el) return; const next = Math.max(0, Math.min(total - 1, idx + dir)); setIdx(next); el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' }); }

  return (
    <div className="kit-carousel">
      <span className="kit-carousel-badge">⭐ {kit.destacado ? 'El más pedido' : kit.tier}</span>
      {pct > 0 && <span className="kit-carousel-badge savings">–{Math.max(12, pct)}% vs súper</span>}
      <button className="kit-carousel-btn prev" onClick={() => mover(-1)} aria-label="Anterior">‹</button>
      <button className="kit-carousel-btn next" onClick={() => mover(1)} aria-label="Siguiente">›</button>
      <div className="kit-carousel-track">
        <div className="kit-carousel-inner" ref={ref} onScroll={onScroll}>
          {slides.map((s, i) => (
            <div
              key={i}
              className={
                'kit-carousel-slide'
                + (s.portada ? ' portada' : '')
                // Con foto (propia o mosaico) la portada usa el fondo CLARO: las fotos de producto
                // vienen sobre blanco y se perderían contra el navy pensado para el emoji dorado.
                + (s.portada && portadaConFoto ? ' portada-foto' : '')
              }
            >
              <div className="kit-carousel-slide-fondo" />
              <span className="kit-carousel-slide-emoji">
                {s.portada
                  ? <PortadaKit imagen={kit.imagen} nombre={kit.nombre} items={items} />
                  : <FotoKit src={s.imagen} emoji={s.emoji} alt={s.nombre} className="kit-carousel-slide-img" />}
              </span>
              <div className="kit-carousel-slide-label">
                <span className="kit-carousel-slide-label-nombre">{s.portada ? kit.nombre : s.nombre}</span>
                <span className="kit-carousel-slide-label-cant">{s.portada ? `${items.length} productos · ${kit.personas}` : `${s.cant} · ${s.det}`}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="kit-carousel-dots">
        {slides.map((_, i) => (
          <button key={i} className={'kit-carousel-dot' + (i === idx ? ' activo' : '')} onClick={() => goTo(i)} aria-label={'Ir al slide ' + (i + 1)} />
        ))}
      </div>
      <div className="kit-carousel-counter"><b>{idx + 1}</b> / {total}</div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="tv5-page-kit-detalle">
      <style>{KD_CSS}</style>
      <TopNav active="kits" accent="teal" />
      <main style={{ maxWidth: 640, margin: '0 auto', padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 64 }}>🧴</div>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: '16px 0 8px' }}>Ese kit no existe</h1>
        <p style={{ color: 'rgba(248,246,241,0.65)', marginBottom: 24 }}>Puede que el link esté viejo. Mirá los 6 kits que tenemos.</p>
        <Link to={TV5_ROUTES.kits} className="kd-cta-suscribir" style={{ display: 'inline-flex' }}>Ver los kits →</Link>
      </main>
      <Footer accent="teal" />
      <BottomNav active="kits" />
    </div>
  );
}

export default function KitDetalle() {
  const { codigo } = useParams();
  const kit = getKit(codigo);
  const [expandida, setExpandida] = useState(false);
  const [dbKit, setDbKit] = useState(null); // productos reales (foto+SKU) desde la DB, o null (fallback emoji)

  // Traemos los productos reales del kit (foto + SKU). Si falla o no matchea el código, quedamos con el
  // fallback emoji de data/kits.js y lo dejamos logueado en consola.
  useEffect(() => {
    if (!codigo) return;
    let vivo = true;
    fetchKitsConProductos()
      .then((map) => {
        if (!vivo) return;
        const match = map[normalizarCodigoKit(codigo)];
        if (match) setDbKit(match);
        else console.warn(`[KitDetalle] sin match de productos reales para "${codigo}" (norm: "${normalizarCodigoKit(codigo)}") → fallback emoji`);
      })
      .catch((e) => { if (vivo) console.warn('[KitDetalle] fetch kits con productos falló → fallback emoji:', e?.message || e); });
    return () => { vivo = false; };
  }, [codigo]);

  if (!kit) return <NotFound />;

  // Precios/composición LIVE desde la DB (misma fuente que la Auditoría). Mientras carga dbKit,
  // cae al estático de data/kits.js (copy marketing: descLarga/FAQ siempre estático).
  const vm = dbKit ? kitViewModel(dbKit) : null;
  const view = {
    ...kit,
    nombre: vm?.nombre ?? kit.nombre,
    tier: vm?.tierLabel ?? kit.tier,
    personas: vm?.personas ?? kit.personas,
    destacado: vm?.destacado ?? kit.destacado,
    precio: vm?.precio ?? kit.precio,
    precioUnica: vm?.precioUnica ?? kit.precioUnica,
    tachado: vm?.tachado ?? kit.tachado,
    ahorro: vm?.ahorro ?? kit.ahorro,
    imagen: vm?.imagen ?? null,
  };
  const pct = vm?.ahorroPct ?? ahorroPct(kit);
  const bonif = vm?.precioBonif ?? precioBonif(kit);
  const tieneAhorro = Number(view.ahorro) > 0;
  const items = buildItems(kit, dbKit);
  const n = items.length;

  return (
    <div className="tv5-page-kit-detalle">
      <style>{KD_CSS}</style>
      <TopNav active="kits" accent="teal" />
      <Breadcrumb backHref={TV5_ROUTES.kits} backLabel="Volver" trail={[{ label: 'Kits', href: TV5_ROUTES.kits }]} current={view.nombre} />

      <main>
        <section className="hero-split">
          <Carrusel kit={view} items={items} pct={pct} />

          <div className="kit-info">
            <div className="kit-tag-row">
              <span className="kit-tag">{view.tier}</span>
              <span className="kit-tag">{view.personas}</span>
              {view.destacado && <span className="kit-tag hot">⭐ El más pedido</span>}
            </div>
            <h1>{view.nombre}</h1>
            <p className="kit-desc">{n} productos {view.descLarga}</p>

            <PrecioBox
              precio={view.precio}
              unidad="/mes suscripción"
              precioTachado={tieneAhorro ? view.tachado : null}
              ahorro={tieneAhorro ? view.ahorro : null}
              ahorroPct={tieneAhorro ? pct : null}
              nota={<>O <b>{fmt(view.precioUnica)} compra única</b> · si preferís probar sin suscribirte</>}
            />

            <PagaCon transferencia={false} />

            <div className="kit-ctas">
              <Link to={`/suscribirme?kit=${normalizarCodigoKit(kit.codigo)}`} className="kd-cta-suscribir" onClick={() => dbgSusc('tap CTA Suscribirme', { kit: kit.codigo, destino: `/suscribirme?kit=${normalizarCodigoKit(kit.codigo)}`, urlActual: window.location.href })}>🎁 Suscribirme al {view.nombre}</Link>
              <Link to={TV5_ROUTES.checkout + '?kit=' + kit.codigo + '&modo=una-vez'} className="kd-cta-unavez" onClick={() => dbgSusc('tap CTA una-vez', { kit: kit.codigo, destino: TV5_ROUTES.checkout + '?kit=' + kit.codigo + '&modo=una-vez', urlActual: window.location.href })}>💳 O comprarlo una sola vez</Link>
            </div>

            <div className="kit-trust">
              <span>Sin permanencia</span>
              <span>Envío gratis día del barrio</span>
              <span>Pausás cuando quieras</span>
              <span>Cambiás el kit mes a mes</span>
            </div>
          </div>
        </section>

        {/* QUÉ TE LLEGA — grid con "Ver los N productos" colapsable */}
        <section className="seccion">
          <h2>Qué te llega <em>cada mes.</em></h2>
          <p className="seccion-sub">{n} productos ya seleccionados por nosotros. Si por stock necesitamos sustituir alguno, siempre lo hacemos por un equivalente de igual o mejor calidad.</p>
          <div className={'incluye-grid' + (expandida ? ' expandido' : '')}>
            {items.map((p, i) => (
              <div key={p.sku || i} className={'incluye-item' + (i >= 6 ? ' extra' : '')}>
                <div className="incluye-foto">
                  <FotoKit src={p.imagen} emoji={p.emoji} alt={p.nombre} className="incluye-foto-img" />
                </div>
                <div className="incluye-info">
                  <div className="incluye-txt">{p.cant} {p.nombre}</div>
                  <div className="incluye-cant">{p.det}{p.real && p.sku ? ` · SKU ${p.sku}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
          {n > 6 && (
            <button className={'incluye-ver-todo' + (expandida ? ' expandido' : '')} onClick={() => setExpandida((v) => !v)}>
              {expandida ? 'Ver menos ' : `Ver los ${n} productos `}<span className="arrow">▾</span>
            </button>
          )}
        </section>

        {/* SE PAGA EN 3 MESES */}
        <section className="seccion">
          <div className="cobro-mini">
            <div>
              <h3>Se paga en <em>3 meses.</em></h3>
              <p>Mes 1 pagás precio compra única. Si seguís, te devolvemos la diferencia en los meses 2 y 3. Terminás pagando lo mismo que 3 meses a precio suscripción.</p>
              <div className="cobro-mini-nums">
                <span className="cobro-mini-num">Mes 1 · <b>{fmt(view.precioUnica)}</b></span>
                <span className="cobro-mini-num">Mes 2 · <b>{fmt(bonif)}</b></span>
                <span className="cobro-mini-num">Mes 3 · <b>{fmt(bonif)}</b></span>
              </div>
            </div>
            <Link to={TV5_ROUTES.comoFunciona} className="cobro-mini-link">Ver detalle →</Link>
          </div>
        </section>

        {/* COMPARACIÓN CON SÚPER */}
        <section className="seccion">
          <div className="comp">
            <div className="comp-cabeza">{view.nombre} vs armarlo suelto en el súper</div>
            <div className="comp-grid">
              <div className="comp-col super">
                <div className="comp-col-lbl">En el súper</div>
                <div className="comp-col-num">{fmt(view.tachado)}</div>
                <ul className="comp-col-list">
                  <li>Tenés que ir vos</li>
                  <li>Cargar bolsas</li>
                  <li>Elegir qué comprar cada mes</li>
                </ul>
              </div>
              <div className="comp-col limpack">
                <div className="comp-col-lbl">Con Limpack</div>
                <div className="comp-col-num">{fmt(view.precio)}</div>
                <ul className="comp-col-list">
                  <li>Llega a tu casa</li>
                  <li>Sin cargar nada</li>
                  <li>Ya elegido, cambiás si querés</li>
                </ul>
              </div>
            </div>
            <div className="comp-final"><b>No te sumes trabajo — sumate tiempo.</b> Ese tiempo lo ganás vos.</div>
          </div>
        </section>

        {/* TESTIMONIALES */}
        <section className="seccion">
          <h2>Lo que dicen los <em>que ya lo probaron.</em></h2>
          <div className="testis">
            {TESTIS.map((t, i) => (
              <div className="testi" key={i}>
                <div className="testi-stars">★★★★★</div>
                <p className="testi-txt">“{t.txt}”</p>
                <div className="testi-autor"><b>{t.autor}</b> · {t.barrio}</div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="seccion">
          <h2>Preguntas <em>rápidas.</em></h2>
          <p className="seccion-sub">Las 3 que más nos hacen. El resto está en la <Link to={TV5_ROUTES.faq} style={{ color: 'var(--tv5kd-teal)', fontWeight: 700 }}>FAQ completa</Link>.</p>
          <FaqMini items={FAQ_KIT} verTodasHref={TV5_ROUTES.faq} />
        </section>

        <CrossLinkBanner accent="gold" title="¿Buscás algo puntual?" sub="Comprá suelto lo que te falte, sin suscripción. 85+ productos elegidos uno por uno." ctaLabel="Ir a la Tienda →" ctaHref={TV5_ROUTES.tienda} />
      </main>

      <ScrollHintPill />
      <MiniBar checkoutHref={TV5_ROUTES.checkout} />
      <Footer accent="teal" />
      <BottomNav active="kits" />
    </div>
  );
}

const KD_CSS = `
.tv5-page-kit-detalle {
  --tv5kd-navy:#1A3461; --tv5kd-teal:#00A8B5; --tv5kd-gold:#C5A059; --tv5kd-green:#10B981; --tv5kd-bone:#F8F6F1;
  --tv5kd-bg:#0A1424; --tv5kd-card:rgba(255,255,255,0.04); --tv5kd-border:rgba(255,255,255,0.08);
  --tv5kd-text:#F8F6F1; --tv5kd-muted:rgba(248,246,241,0.65); --tv5kd-dim:rgba(248,246,241,0.45);
  --safe-bottom:env(safe-area-inset-bottom,0px);
  background:var(--tv5kd-bg); color:var(--tv5kd-text); min-height:100vh; position:relative;
  overflow-x:clip; padding-bottom:calc(72px + var(--safe-bottom));
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
}
.tv5-page-kit-detalle::before { content:''; position:fixed; inset:0; z-index:0; pointer-events:none;
  background:radial-gradient(circle at 20% 10%, rgba(0,168,181,0.10) 0%, transparent 40%), radial-gradient(circle at 80% 60%, rgba(197,160,89,0.08) 0%, transparent 40%); }
.tv5-page-kit-detalle main { position:relative; z-index:1; }
@media (min-width:900px){ .tv5-page-kit-detalle { padding-bottom:0; } }

.tv5-page-kit-detalle .hero-split { max-width:1200px; margin:20px auto 0; padding:20px; display:grid; grid-template-columns:1fr; gap:24px; }
.tv5-page-kit-detalle .hero-split > * { min-width:0; }
@media (min-width:900px){ .tv5-page-kit-detalle .hero-split { grid-template-columns:1fr 1fr; gap:40px; padding:30px 20px; align-items:center; } }

/* CARRUSEL */
.tv5-page-kit-detalle .kit-carousel { position:relative; }
.tv5-page-kit-detalle .kit-carousel-track { width:100%; aspect-ratio:1/1; max-height:560px; border-radius:24px; overflow:hidden; position:relative; background:transparent; }
@media (max-width:640px){ .tv5-page-kit-detalle .kit-carousel-track { aspect-ratio:4/3; max-height:300px; } .tv5-page-kit-detalle .kit-carousel-slide-emoji { font-size:108px; } .tv5-page-kit-detalle .kit-carousel-slide.portada .kit-carousel-slide-emoji { font-size:80px; } }
.tv5-page-kit-detalle .kit-carousel-inner { width:100%; height:100%; display:flex; overflow-x:auto; overflow-y:hidden; scroll-snap-type:x mandatory; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.tv5-page-kit-detalle .kit-carousel-inner::-webkit-scrollbar { display:none; }
.tv5-page-kit-detalle .kit-carousel-slide { flex:0 0 100%; width:100%; height:100%; scroll-snap-align:start; position:relative; box-sizing:border-box; }
.tv5-page-kit-detalle .kit-carousel-slide-fondo { position:absolute; inset:0; background:linear-gradient(160deg, #F8F6F1 0%, #EDE9DF 100%); }
.tv5-page-kit-detalle .kit-carousel-slide-emoji { position:relative; z-index:1; width:100%; height:100%; display:flex; align-items:center; justify-content:center; font-size:140px; line-height:1; filter:drop-shadow(0 12px 20px rgba(0,0,0,0.15)); }
.tv5-page-kit-detalle .kit-carousel-slide-img { width:82%; height:82%; object-fit:contain; filter:drop-shadow(0 12px 20px rgba(0,0,0,0.18)); }
/* Mosaico de portada: fallback cuando el kit no tiene foto propia (usa las de sus productos). */
.tv5-page-kit-detalle .kit-portada-mosaico { width:86%; height:86%; display:grid; grid-template-columns:1fr 1fr; grid-template-rows:1fr 1fr; gap:6%; }
.tv5-page-kit-detalle .kit-portada-celda { display:flex; align-items:center; justify-content:center; overflow:hidden; }
.tv5-page-kit-detalle .kit-portada-celda img { width:100%; height:100%; object-fit:contain; filter:drop-shadow(0 8px 14px rgba(0,0,0,0.15)); }
.tv5-page-kit-detalle .kit-carousel-slide-label { position:absolute; bottom:20px; left:20px; right:20px; z-index:3; background:rgba(10,20,36,0.85); backdrop-filter:blur(12px); color:var(--tv5kd-text); padding:10px 14px; border-radius:10px; display:flex; justify-content:space-between; align-items:center; gap:10px; overflow:hidden; }
.tv5-page-kit-detalle .kit-carousel-slide-label-nombre { font-size:13px; font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; flex:1; }
.tv5-page-kit-detalle .kit-carousel-slide-label-cant { font-size:11px; color:var(--tv5kd-muted); font-weight:600; white-space:nowrap; flex-shrink:0; }
.tv5-page-kit-detalle .kit-carousel-slide.portada .kit-carousel-slide-fondo { background:linear-gradient(135deg, var(--tv5kd-navy) 0%, #0F2242 100%); }
/* Portada CON foto: fondo claro (igual que las slides de producto) para que las fotos se lean. */
.tv5-page-kit-detalle .kit-carousel-slide.portada.portada-foto .kit-carousel-slide-fondo { background:linear-gradient(160deg, #F8F6F1 0%, #EDE9DF 100%); }
.tv5-page-kit-detalle .kit-carousel-slide.portada.portada-foto .kit-carousel-slide-label { background:rgba(10,20,36,0.85); backdrop-filter:blur(12px); padding:10px 14px; border-radius:10px; }
.tv5-page-kit-detalle .kit-carousel-slide.portada.portada-foto .kit-carousel-slide-label-nombre { color:var(--tv5kd-text); }
.tv5-page-kit-detalle .kit-carousel-slide.portada.portada-foto .kit-carousel-slide-label-cant { color:var(--tv5kd-gold); }
.tv5-page-kit-detalle .kit-carousel-slide.portada .kit-carousel-slide-emoji { color:var(--tv5kd-gold); font-size:100px; }
.tv5-page-kit-detalle .kit-carousel-slide.portada .kit-carousel-slide-label { background:transparent; backdrop-filter:none; text-align:center; flex-direction:column; gap:4px; padding:0; }
.tv5-page-kit-detalle .kit-carousel-slide.portada .kit-carousel-slide-label-nombre { font-size:clamp(18px,4vw,24px); color:var(--tv5kd-bone); letter-spacing:-0.02em; font-weight:800; white-space:normal; overflow:visible; }
.tv5-page-kit-detalle .kit-carousel-slide.portada .kit-carousel-slide-label-cant { color:var(--tv5kd-gold); letter-spacing:0.05em; text-transform:uppercase; white-space:normal; }
.tv5-page-kit-detalle .kit-carousel-badge { position:absolute; top:20px; left:20px; z-index:3; background:linear-gradient(135deg, var(--tv5kd-gold) 0%, #D4B370 100%); color:var(--tv5kd-navy); font-size:11px; font-weight:800; letter-spacing:0.1em; text-transform:uppercase; padding:8px 14px; border-radius:999px; box-shadow:0 8px 20px rgba(197,160,89,0.4); pointer-events:none; }
.tv5-page-kit-detalle .kit-carousel-badge.savings { top:auto; bottom:20px; left:auto; right:20px; background:linear-gradient(135deg, var(--tv5kd-green) 0%, #34D399 100%); color:#052E1B; }
.tv5-page-kit-detalle .kit-carousel-btn { position:absolute; top:50%; transform:translateY(-50%); z-index:3; width:40px; height:40px; border-radius:50%; background:rgba(10,20,36,0.75); backdrop-filter:blur(12px); border:1px solid var(--tv5kd-border); color:var(--tv5kd-text); font-size:16px; font-weight:700; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.2s; }
.tv5-page-kit-detalle .kit-carousel-btn.prev { left:12px; } .tv5-page-kit-detalle .kit-carousel-btn.next { right:12px; }
@media (max-width:700px){ .tv5-page-kit-detalle .kit-carousel-btn { display:none; } }
@media (hover:hover){ .tv5-page-kit-detalle .kit-carousel-btn:hover { background:rgba(0,168,181,0.35); border-color:var(--tv5kd-teal); transform:translateY(-50%) scale(1.06); } }
.tv5-page-kit-detalle .kit-carousel-dots { display:flex; gap:6px; justify-content:center; margin-top:14px; flex-wrap:wrap; }
.tv5-page-kit-detalle .kit-carousel-dot { width:auto; height:auto; padding:12px 5px; background:transparent; border:0; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; -webkit-tap-highlight-color:transparent; }
.tv5-page-kit-detalle .kit-carousel-dot::before { content:''; display:block; width:8px; height:8px; border-radius:50%; background:rgba(255,255,255,0.15); transition:all 0.2s; }
.tv5-page-kit-detalle .kit-carousel-dot.activo::before { background:var(--tv5kd-teal); width:24px; border-radius:999px; }
.tv5-page-kit-detalle .kit-carousel-counter { text-align:center; margin-top:8px; font-size:11px; color:var(--tv5kd-dim); letter-spacing:0.05em; }
.tv5-page-kit-detalle .kit-carousel-counter b { color:var(--tv5kd-muted); font-weight:700; }

/* INFO */
.tv5-page-kit-detalle .kit-info { display:flex; flex-direction:column; gap:14px; }
.tv5-page-kit-detalle .kit-tag-row { display:flex; gap:8px; flex-wrap:wrap; }
.tv5-page-kit-detalle .kit-tag { display:inline-block; font-size:11px; font-weight:800; letter-spacing:0.12em; text-transform:uppercase; padding:6px 12px; border-radius:999px; background:rgba(0,168,181,0.12); color:var(--tv5kd-teal); border:1px solid rgba(0,168,181,0.3); }
.tv5-page-kit-detalle .kit-tag.hot { background:linear-gradient(135deg, #FF6B35 0%, #F7931E 100%); color:#fff; border-color:transparent; }
.tv5-page-kit-detalle .kit-info h1 { font-size:clamp(30px,6vw,44px); font-weight:800; letter-spacing:-0.03em; line-height:1.1; }
.tv5-page-kit-detalle .kit-desc { font-size:15px; color:var(--tv5kd-muted); line-height:1.55; }

/* CTAs */
.tv5-page-kit-detalle .kit-ctas { display:flex; flex-direction:column; gap:10px; margin-top:14px; }
.tv5-page-kit-detalle .kd-cta-suscribir { display:flex; align-items:center; justify-content:center; gap:8px; background:linear-gradient(135deg, var(--tv5kd-teal) 0%, #00C4D4 100%); color:#0A1424; padding:18px 24px; border-radius:14px; font-weight:800; font-size:16px; text-decoration:none; box-shadow:0 12px 32px rgba(0,168,181,0.35); transition:transform 0.2s; }
.tv5-page-kit-detalle .kd-cta-suscribir:active { transform:translateY(-2px); }
.tv5-page-kit-detalle .kd-cta-unavez { display:flex; align-items:center; justify-content:center; gap:8px; background:rgba(255,255,255,0.05); border:1px solid var(--tv5kd-border); color:var(--tv5kd-text); padding:14px 20px; border-radius:14px; font-weight:700; font-size:14px; text-decoration:none; transition:all 0.2s; }
@media (hover:hover){ .tv5-page-kit-detalle .kd-cta-suscribir:hover { transform:translateY(-2px); } .tv5-page-kit-detalle .kd-cta-unavez:hover { background:rgba(255,255,255,0.08); border-color:var(--tv5kd-teal); color:var(--tv5kd-teal); } }

/* TRUST */
.tv5-page-kit-detalle .kit-trust { display:flex; flex-wrap:wrap; gap:12px; margin-top:14px; }
.tv5-page-kit-detalle .kit-trust span { font-size:12px; color:var(--tv5kd-muted); display:inline-flex; align-items:center; gap:4px; }
.tv5-page-kit-detalle .kit-trust span::before { content:'✓'; color:var(--tv5kd-teal); font-weight:800; }

/* SECCIÓN */
.tv5-page-kit-detalle .seccion { max-width:1200px; margin:50px auto 0; padding:0 20px; }
.tv5-page-kit-detalle .seccion h2 { font-size:clamp(24px,4vw,32px); font-weight:800; letter-spacing:-0.02em; margin-bottom:8px; }
.tv5-page-kit-detalle .seccion h2 em { color:var(--tv5kd-teal); font-style:italic; }
.tv5-page-kit-detalle .seccion-sub { font-size:15px; color:var(--tv5kd-muted); line-height:1.5; margin-bottom:24px; }

/* INCLUYE GRID */
.tv5-page-kit-detalle .incluye-grid { display:grid; grid-template-columns:1fr; gap:10px; }
@media (min-width:480px){ .tv5-page-kit-detalle .incluye-grid { grid-template-columns:repeat(2,1fr); } }
@media (min-width:720px){ .tv5-page-kit-detalle .incluye-grid { grid-template-columns:repeat(3,1fr); gap:12px; } }
@media (min-width:1024px){ .tv5-page-kit-detalle .incluye-grid { grid-template-columns:repeat(4,1fr); } }
@media (max-width:719px){ .tv5-page-kit-detalle .incluye-item.extra { display:none; } .tv5-page-kit-detalle .incluye-grid.expandido .incluye-item.extra { display:flex; animation:tv5kdFade 0.4s ease; } }
@keyframes tv5kdFade { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
.tv5-page-kit-detalle .incluye-ver-todo { display:none; margin:14px auto 0; background:rgba(0,168,181,0.12); border:1px solid rgba(0,168,181,0.35); color:var(--tv5kd-teal); padding:12px 22px; border-radius:999px; font-size:13px; font-weight:800; cursor:pointer; text-align:center; transition:all 0.2s; width:fit-content; }
.tv5-page-kit-detalle .incluye-ver-todo .arrow { display:inline-block; margin-left:6px; transition:transform 0.3s; }
.tv5-page-kit-detalle .incluye-ver-todo.expandido .arrow { transform:rotate(180deg); }
@media (max-width:719px){ .tv5-page-kit-detalle .incluye-ver-todo { display:block; } }
.tv5-page-kit-detalle .incluye-item { background:var(--tv5kd-card); border:1px solid var(--tv5kd-border); border-radius:14px; padding:12px; display:flex; align-items:center; gap:12px; transition:all 0.2s; }
.tv5-page-kit-detalle .incluye-foto { width:52px; height:52px; flex-shrink:0; border-radius:10px; background:linear-gradient(160deg, #F8F6F1 0%, #EDE9DF 100%); display:flex; align-items:center; justify-content:center; font-size:30px; line-height:1; box-shadow:0 4px 12px rgba(0,0,0,0.15), inset 0 -1px 0 rgba(0,0,0,0.08); overflow:hidden; }
.tv5-page-kit-detalle .incluye-foto-img { width:100%; height:100%; object-fit:contain; padding:4px; box-sizing:border-box; }
.tv5-page-kit-detalle .incluye-info { min-width:0; flex:1; }
.tv5-page-kit-detalle .incluye-txt { font-size:13px; color:var(--tv5kd-text); font-weight:700; line-height:1.25; letter-spacing:-0.01em; }
.tv5-page-kit-detalle .incluye-cant { font-size:11px; color:var(--tv5kd-dim); font-weight:500; margin-top:3px; }
@media (hover:hover){ .tv5-page-kit-detalle .incluye-item:hover { background:rgba(255,255,255,0.06); border-color:rgba(0,168,181,0.25); transform:translateY(-2px); } .tv5-page-kit-detalle .incluye-ver-todo:hover { background:rgba(0,168,181,0.22); transform:translateY(-1px); } }

/* COBRO MINI */
.tv5-page-kit-detalle .cobro-mini { background:linear-gradient(135deg, rgba(0,168,181,.08) 0%, rgba(197,160,89,.08) 100%); border:1px solid rgba(0,168,181,.2); border-radius:22px; padding:28px 24px; display:grid; grid-template-columns:1fr; gap:20px; align-items:center; }
@media (min-width:720px){ .tv5-page-kit-detalle .cobro-mini { grid-template-columns:1fr auto; padding:32px 40px; } }
.tv5-page-kit-detalle .cobro-mini h3 { font-size:20px; font-weight:800; letter-spacing:-0.01em; margin-bottom:8px; }
.tv5-page-kit-detalle .cobro-mini h3 em { color:var(--tv5kd-teal); font-style:italic; }
.tv5-page-kit-detalle .cobro-mini p { font-size:14px; color:var(--tv5kd-muted); line-height:1.55; margin-bottom:12px; }
.tv5-page-kit-detalle .cobro-mini-nums { display:flex; gap:10px; flex-wrap:wrap; font-size:13px; }
.tv5-page-kit-detalle .cobro-mini-num { background:rgba(255,255,255,0.05); padding:8px 14px; border-radius:999px; border:1px solid var(--tv5kd-border); }
.tv5-page-kit-detalle .cobro-mini-num b { color:var(--tv5kd-text); font-weight:800; }
.tv5-page-kit-detalle .cobro-mini-link { display:inline-flex; align-items:center; gap:6px; background:rgba(0,168,181,0.15); border:1px solid rgba(0,168,181,0.4); color:var(--tv5kd-teal); padding:12px 20px; border-radius:12px; font-weight:700; font-size:13px; text-decoration:none; white-space:nowrap; min-height:44px; transition:all 0.2s; }
@media (hover:hover){ .tv5-page-kit-detalle .cobro-mini-link:hover { background:rgba(0,168,181,0.25); transform:translateX(2px); } }

/* COMPARACIÓN CON SÚPER */
.tv5-page-kit-detalle .comp { background:var(--tv5kd-card); border:1px solid var(--tv5kd-border); border-radius:22px; padding:28px 22px; }
.tv5-page-kit-detalle .comp-cabeza { font-size:12px; color:var(--tv5kd-muted); letter-spacing:0.08em; text-transform:uppercase; text-align:center; margin-bottom:22px; font-weight:700; }
.tv5-page-kit-detalle .comp-grid { display:grid; grid-template-columns:1fr; gap:16px; margin-bottom:20px; }
@media (min-width:640px){ .tv5-page-kit-detalle .comp-grid { grid-template-columns:repeat(2,1fr); } }
.tv5-page-kit-detalle .comp-col { text-align:center; padding:22px 16px; border-radius:16px; background:rgba(255,255,255,0.03); border:1px solid var(--tv5kd-border); position:relative; }
.tv5-page-kit-detalle .comp-col.limpack { background:rgba(0,168,181,0.08); border-color:rgba(0,168,181,0.3); }
.tv5-page-kit-detalle .comp-col.limpack::before { content:'Vos'; position:absolute; top:-10px; left:50%; transform:translateX(-50%); background:var(--tv5kd-teal); color:#0A1424; font-size:10px; font-weight:900; padding:3px 12px; border-radius:999px; letter-spacing:0.1em; }
.tv5-page-kit-detalle .comp-col-lbl { font-size:12px; color:var(--tv5kd-muted); font-weight:700; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:6px; }
.tv5-page-kit-detalle .comp-col-num { font-size:28px; font-weight:900; color:var(--tv5kd-text); letter-spacing:-0.02em; margin-bottom:12px; }
.tv5-page-kit-detalle .comp-col.limpack .comp-col-num { color:var(--tv5kd-teal); }
.tv5-page-kit-detalle .comp-col-list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:8px; }
.tv5-page-kit-detalle .comp-col-list li { font-size:13px; color:var(--tv5kd-muted); display:flex; align-items:center; justify-content:center; gap:6px; }
.tv5-page-kit-detalle .comp-col.super .comp-col-list li::before { content:'✕'; color:#F87171; font-weight:800; }
.tv5-page-kit-detalle .comp-col.limpack .comp-col-list li::before { content:'✓'; color:var(--tv5kd-teal); font-weight:800; }
.tv5-page-kit-detalle .comp-final { text-align:center; padding-top:20px; border-top:1px dashed var(--tv5kd-border); font-size:14px; color:var(--tv5kd-muted); }
.tv5-page-kit-detalle .comp-final b { color:var(--tv5kd-text); }

/* TESTIMONIALES */
.tv5-page-kit-detalle .testis { display:grid; grid-template-columns:1fr; gap:12px; }
@media (min-width:720px){ .tv5-page-kit-detalle .testis { grid-template-columns:repeat(3,1fr); } }
.tv5-page-kit-detalle .testi { background:var(--tv5kd-card); border:1px solid var(--tv5kd-border); border-radius:18px; padding:22px 20px; }
.tv5-page-kit-detalle .testi-stars { color:var(--tv5kd-gold); font-size:13px; margin-bottom:10px; letter-spacing:0.05em; }
.tv5-page-kit-detalle .testi-txt { font-size:14px; color:var(--tv5kd-text); line-height:1.5; margin-bottom:14px; }
.tv5-page-kit-detalle .testi-autor { font-size:12px; color:var(--tv5kd-dim); }
.tv5-page-kit-detalle .testi-autor b { color:var(--tv5kd-muted); font-weight:700; }

@media (prefers-reduced-motion:reduce){ .tv5-page-kit-detalle .incluye-grid.expandido .incluye-item.extra { animation:none; } }
`;
